import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui';
import { useNotificationRecord, updateNotificationRecord } from './useData';
import { useNotification } from '../../contexts/NotificationContext';
import { useTheme } from '../../contexts/ThemeContext';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';

const Form: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { showSuccess, showError } = useNotification();
  const { colors } = useTheme();
  const notificationId = id ? parseInt(id, 10) : null;

  const { data: item, isLoading: loadingItem, mutate } = useNotificationRecord(notificationId ?? null);

  const [message, setMessage] = useState('');
  const [link, setLink] = useState('');
  const [markAsRead, setMarkAsRead] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setMessage(item.message ?? '');
      setLink(item.link ?? '');
      setMarkAsRead(!!item.readAt);
    }
  }, [item]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (notificationId == null) return;
    setSaving(true);
    try {
      await updateNotificationRecord(notificationId, {
        message: message.trim() || undefined,
        link: link.trim() || undefined,
        readAt: markAsRead ? new Date().toISOString() : null,
      });
      await mutate();
      showSuccess('Notification updated.');
      navigate('/notifications');
    } catch (err: any) {
      showError(err.response?.data?.message || err.message || 'Failed to update notification.');
    } finally {
      setSaving(false);
    }
  };

  if (loadingItem || !notificationId) {
    return (
      <div className="flex items-center justify-center min-h-[200px]">
        <LoadingSpinner />
      </div>
    );
  }

  if (!item) {
    return (
      <div className="p-6">
        <p className={colors.textSecondary}>Notification not found.</p>
        <Button variant="secondary" onClick={() => navigate('/notifications')} className="mt-4">
          Back to list
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl">
      <h1 className="text-xl font-semibold mb-4" style={{ color: colors.text }}>Edit notification</h1>
      <form onSubmit={handleSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
            Type
          </label>
          <p className="py-2 text-sm" style={{ color: colors.text }}>{item.entityType} (ID: {item.entityId})</p>
        </div>
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
            Message
          </label>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={4}
            className="w-full border rounded-lg px-3 py-2"
            style={{ borderColor: colors.border, backgroundColor: colors.background, color: colors.text }}
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-1" style={{ color: colors.textSecondary }}>
            Link
          </label>
          <input
            type="text"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            className="w-full border rounded-lg px-3 py-2"
            style={{ borderColor: colors.border, backgroundColor: colors.background, color: colors.text }}
            placeholder="Optional URL"
          />
        </div>
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="read"
            checked={markAsRead}
            onChange={(e) => setMarkAsRead(e.target.checked)}
            className="rounded border-gray-300"
          />
          <label htmlFor="read" className="text-sm" style={{ color: colors.text }}>
            Mark as read
          </label>
        </div>
        <div className="flex gap-2 pt-2">
          <Button type="submit" variant="accent" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/notifications')}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default Form;
